import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { firmHasPartialPayments } from '@/config/firm-features';
import { format, parseISO } from 'date-fns';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { MatterCombobox, ClientCombobox } from '@/components/common/EntityCombobox';
import { DatePicker } from '@/components/ui/date-picker';
import { Save, Calculator, FileText, CreditCard, Receipt, AlertCircle, AlertTriangle } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { UnifiedInvoiceAPI } from '@/services/invoicing';
import { UnifiedInvoice, CREDIT_REASONS, VAT_TYPES } from '@/types/invoicing';
import { useStore } from '@/lib/store';
import { formatCurrency } from '@/utils/document';


interface EditInvoiceDialogProps {
  isOpen: boolean;
  onClose: () => void;
  invoice: UnifiedInvoice | null;
  onInvoiceUpdated: () => void;
}

const INVOICE_TYPE_OPTIONS = [
  { value: 'invoice', label: 'Invoice' },
  { value: 'credit_note', label: 'Credit Note' },
  { value: 'downpayment', label: 'Down Payment' },
];

export function EditInvoiceDialog({
  isOpen,
  onClose,
  invoice,
  onInvoiceUpdated,
}: EditInvoiceDialogProps) {
  const { clients, matters, fetchClients, fetchMatters } = useStore();
  const [isSaving, setIsSaving] = useState(false);

  // Identity / advanced fields
  const [clientId, setClientId] = useState('');
  const [matterId, setMatterId] = useState('');
  const [invoiceType, setInvoiceType] = useState('');

  // Financial fields
  const [subtotal, setSubtotal] = useState('');
  const [serviceCharge, setServiceCharge] = useState('');
  const [feeSplitPercentage, setFeeSplitPercentage] = useState('');
  const [feeSplitAmount, setFeeSplitAmount] = useState('');
  const [vatType, setVatType] = useState('');
  const [vatRate, setVatRate] = useState('');
  const [vatAmount, setVatAmount] = useState('');
  const [total, setTotal] = useState('');

  // Balance fields
  const [appliedDownPaymentsAmount, setAppliedDownPaymentsAmount] = useState('');
  const [appliedCreditNotesAmount, setAppliedCreditNotesAmount] = useState('');

  // Date fields
  const [issueDate, setIssueDate] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [paymentDate, setPaymentDate] = useState('');

  // Other fields
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [description, setDescription] = useState('');
  const [creditReason, setCreditReason] = useState('');
  const [status, setStatus] = useState('');
  const [amountPaid, setAmountPaid] = useState('');
  const { user } = useAuth();
  const partialPayments = firmHasPartialPayments(user?.firms);
  const [sendEmailNotification, setSendEmailNotification] = useState(false);
  const [deductFromNextInvoice, setDeductFromNextInvoice] = useState(false);

  // Track which fields have been modified
  const [dirtyFields, setDirtyFields] = useState<Set<string>>(new Set());

  // Load clients and matters when dialog opens
  useEffect(() => {
    if (isOpen) {
      if (clients.length === 0) fetchClients();
      if (matters.length === 0) fetchMatters();
    }
  }, [isOpen]);

  // Populate form from invoice
  useEffect(() => {
    if (invoice && isOpen) {
      setClientId(String(invoice.client ?? ''));
      setMatterId(String(invoice.matter ?? ''));
      setInvoiceType(invoice.invoice_type ?? 'invoice');
      setSubtotal(String(invoice.subtotal ?? ''));
      setServiceCharge(String(invoice.service_charge ?? ''));
      setFeeSplitPercentage(String(invoice.fee_split_percentage ?? ''));
      setFeeSplitAmount(String(invoice.fee_split_amount ?? ''));
      setVatType(invoice.vat_type ?? '');
      setVatRate(String(invoice.vat_rate ?? ''));
      setVatAmount(String(invoice.vat_amount ?? ''));
      setTotal(String(invoice.total ?? ''));
      setAppliedDownPaymentsAmount(String(invoice.applied_down_payments_amount ?? '0'));
      setAppliedCreditNotesAmount(String(invoice.applied_credit_notes_amount ?? '0'));
      setIssueDate(invoice.issue_date ?? '');
      setDueDate(invoice.due_date ?? '');
      setPaymentDate(invoice.payment_date ?? '');
      setInvoiceNumber(invoice.invoice_number ?? '');
      setDescription(invoice.description ?? '');
      setCreditReason(invoice.credit_reason ?? '');
      setStatus(invoice.status ?? '');
      setAmountPaid(invoice.amount_paid != null ? String(invoice.amount_paid) : '');
      setSendEmailNotification(invoice.send_email_notification ?? false);
      setDeductFromNextInvoice(invoice.deduct_from_next_invoice ?? false);
      setDirtyFields(new Set());

      // Auto-calculate Total and Service Charge for fee-split invoices
      const fsp = Number(invoice.fee_split_percentage) || 0;
      if (fsp > 0) {
        const fsa = Number(invoice.fee_split_amount) || 0;
        if (fsa > 0) {
          setTotal((fsa / fsp * 100).toFixed(2));
        }
        const sc = Number(invoice.service_charge) || 0;
        if (sc > 0) {
          setServiceCharge((sc * fsp / 100).toFixed(2));
        }
      }
    }
  }, [invoice, isOpen]);

  const markDirty = (field: string) => {
    setDirtyFields(prev => new Set(prev).add(field));
  };

  // Auto-calculate total when subtotal/VAT changes (if user hasn't manually set total)
  const calculatedTotal = useMemo(() => {
    const fsp = Number(feeSplitPercentage) || 0;
    const fsa = Number(feeSplitAmount) || 0;
    if (fsp > 0 && fsa > 0) {
      return (fsa / fsp * 100).toFixed(2);
    }
    const sub = Number(subtotal) || 0;
    const sc = Number(serviceCharge) || 0;
    const vat = Number(vatAmount) || 0;
    return (sub + sc + vat).toFixed(2);
  }, [subtotal, serviceCharge, vatAmount, feeSplitPercentage, feeSplitAmount]);

  // Auto-calculate VAT amount when subtotal/rate changes
  const calculatedVatAmount = useMemo(() => {
    const sub = Number(subtotal) || 0;
    const sc = Number(serviceCharge) || 0;
    const rate = Number(vatRate) || 0;
    return ((sub + sc) * rate / 100).toFixed(2);
  }, [subtotal, serviceCharge, vatRate]);

  // Calculate balance due
  const calculatedBalanceDue = useMemo(() => {
    const t = Number(total) || 0;
    const dp = Number(appliedDownPaymentsAmount) || 0;
    const cn = Number(appliedCreditNotesAmount) || 0;
    return (t - dp - cn).toFixed(2);
  }, [total, appliedDownPaymentsAmount, appliedCreditNotesAmount]);

  const handleRecalculate = () => {
    if (!dirtyFields.has('vat_amount')) {
      setVatAmount(calculatedVatAmount);
      markDirty('vat_amount');
    }
    if (!dirtyFields.has('total')) {
      const fsp = Number(feeSplitPercentage) || 0;
      const fsa = Number(feeSplitAmount) || 0;
      if (fsp > 0 && fsa > 0) {
        setTotal((fsa / fsp * 100).toFixed(2));
      } else {
        const sub = Number(subtotal) || 0;
        const sc = Number(serviceCharge) || 0;
        const va = Number(dirtyFields.has('vat_amount') ? vatAmount : calculatedVatAmount) || 0;
        setTotal((sub + sc + va).toFixed(2));
      }
      markDirty('total');
    }
  };

  const handleSave = async () => {
    if (!invoice) return;

    setIsSaving(true);
    try {
      // Only send fields that were actually modified
      const updates: Record<string, any> = {};

      if (dirtyFields.has('client')) updates.client = Number(clientId);
      if (dirtyFields.has('matter')) updates.matter = Number(matterId);
      if (dirtyFields.has('invoice_type')) updates.invoice_type = invoiceType;
      if (dirtyFields.has('subtotal')) updates.subtotal = subtotal;
      if (dirtyFields.has('service_charge')) {
        // Service charge is already a per-participant amount; send as-is
        updates.service_charge = serviceCharge;
      }
      if (dirtyFields.has('fee_split_percentage')) updates.fee_split_percentage = Number(feeSplitPercentage);
      if (dirtyFields.has('fee_split_amount')) updates.fee_split_amount = feeSplitAmount;
      if (dirtyFields.has('vat_type')) updates.vat_type = vatType;
      if (dirtyFields.has('vat_rate')) updates.vat_rate = vatRate;
      if (dirtyFields.has('vat_amount')) updates.vat_amount = vatAmount;
      if (dirtyFields.has('total')) updates.total = total;
      if (dirtyFields.has('applied_down_payments_amount')) updates.applied_down_payments_amount = appliedDownPaymentsAmount;
      if (dirtyFields.has('applied_credit_notes_amount')) updates.applied_credit_notes_amount = appliedCreditNotesAmount;
      if (dirtyFields.has('issue_date')) updates.issue_date = issueDate;
      if (dirtyFields.has('due_date')) updates.due_date = dueDate;
      if (dirtyFields.has('payment_date')) updates.payment_date = paymentDate || null;
      if (dirtyFields.has('invoice_number')) updates.invoice_number = invoiceNumber;
      if (dirtyFields.has('description')) updates.description = description;
      if (dirtyFields.has('credit_reason')) updates.credit_reason = creditReason;
      if (dirtyFields.has('status')) updates.status = status;
      if (dirtyFields.has('amount_paid')) updates.amount_paid = Number(amountPaid.replace(',', '.')) || 0;
      if (dirtyFields.has('send_email_notification')) updates.send_email_notification = sendEmailNotification;
      if (dirtyFields.has('deduct_from_next_invoice')) updates.deduct_from_next_invoice = deductFromNextInvoice;

      if (Object.keys(updates).length === 0) {
        toast({ title: 'No changes', description: 'No fields were modified.' });
        return;
      }

      await UnifiedInvoiceAPI.updateInvoice(invoice.id, updates);

      toast({
        title: 'Invoice updated',
        description: `${invoice.invoice_number} has been updated successfully.`,
      });

      onInvoiceUpdated();
      onClose();
    } catch (error: any) {
      console.error('Failed to update invoice:', error);
      const errorMessage = error?.response?.data?.error || error?.response?.data?.detail || 'Failed to update invoice';
      toast({
        title: 'Error',
        description: typeof errorMessage === 'string' ? errorMessage : 'Failed to update invoice',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (!invoice) return null;

  const currentType = dirtyFields.has('invoice_type') ? invoiceType : invoice.invoice_type;
  const typeLabel = currentType === 'credit_note' ? 'Credit Note' :
                    currentType === 'downpayment' ? 'Down Payment' : 'Invoice';
  
  const TypeIcon = currentType === 'credit_note' ? CreditCard :
                   currentType === 'downpayment' ? Receipt : FileText;

  const hasChanges = dirtyFields.size > 0;
  const hasAdvancedChanges = dirtyFields.has('client') || dirtyFields.has('matter') || dirtyFields.has('invoice_type');

  const statusOptions = currentType === 'invoice'
    ? ['draft', 'sent', 'paid', ...(partialPayments ? ['partly_paid'] : []), 'overdue', 'cancelled']
    : currentType === 'credit_note'
    ? ['draft', 'sent', 'applied', 'cancelled']
    : ['draft', 'sent', 'paid', 'applied', 'cancelled'];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <TypeIcon className="h-5 w-5" />
            Edit {typeLabel}: {invoice.invoice_number}
          </DialogTitle>
          <DialogDescription className="flex items-center gap-2 flex-wrap">
            {hasChanges && (
              <Badge variant="outline" className="text-amber-600 border-amber-300">
                <AlertCircle className="h-3 w-3 mr-1" />
                {dirtyFields.size} unsaved change{dirtyFields.size !== 1 ? 's' : ''}
              </Badge>
            )}
            {hasAdvancedChanges && (
              <Badge variant="outline" className="text-red-600 border-red-300">
                <AlertTriangle className="h-3 w-3 mr-1" />
                Advanced fields changed
              </Badge>
            )}
            Modify any field below. Only changed fields will be sent.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* ── Identity & Advanced ── */}
          <section>
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">Identity & Assignment</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="edit-invoice-number">Invoice Number</Label>
                <Input
                  id="edit-invoice-number"
                  value={invoiceNumber}
                  onChange={(e) => { setInvoiceNumber(e.target.value); markDirty('invoice_number'); }}
                  className={dirtyFields.has('invoice_number') ? 'border-amber-400' : ''}
                  placeholder="Invoice number"
                />
              </div>
              <div>
                <Label htmlFor="edit-invoice-type">Invoice Type</Label>
                <Select
                  value={invoiceType}
                  onValueChange={(v) => { setInvoiceType(v); markDirty('invoice_type'); }}
                >
                  <SelectTrigger className={dirtyFields.has('invoice_type') ? 'border-amber-400' : ''}>
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {INVOICE_TYPE_OPTIONS.map(t => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 mt-4">
              <div>
                <Label>Client</Label>
                <ClientCombobox
                  clients={clients}
                  value={clientId ? Number(clientId) : null}
                  onChange={(id) => { setClientId(id ? String(id) : ''); markDirty('client'); }}
                  triggerClassName={dirtyFields.has('client') ? 'border-amber-400' : ''}
                />
              </div>
              <div>
                <Label>Matter</Label>
                <MatterCombobox
                  matters={matters}
                  includeInactive
                  value={matterId ? Number(matterId) : null}
                  onChange={(id) => { setMatterId(id ? String(id) : ''); markDirty('matter'); }}
                  triggerClassName={dirtyFields.has('matter') ? 'border-amber-400' : ''}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 mt-4">
              <div>
                <Label htmlFor="edit-status">Status</Label>
                <Select
                  value={status}
                  onValueChange={(v) => { setStatus(v); markDirty('status'); }}
                >
                  <SelectTrigger className={dirtyFields.has('status') ? 'border-amber-400' : ''}>
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    {statusOptions.map(s => (
                      <SelectItem key={s} value={s}>
                        {(s.charAt(0).toUpperCase() + s.slice(1)).replace(/_/g, ' ')}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1">
                  Use "Update Status" action for side effects (e.g. cancel releases timesheets)
                </p>
                {partialPayments && status === 'partly_paid' && (
                  <div className="mt-2">
                    <Label htmlFor="edit-amount-paid">Amount paid</Label>
                    <Input
                      id="edit-amount-paid"
                      type="number"
                      min="0"
                      step="0.01"
                      value={amountPaid}
                      onChange={(e) => { setAmountPaid(e.target.value); markDirty('amount_paid'); }}
                      className={dirtyFields.has('amount_paid') ? 'border-amber-400' : ''}
                    />
                  </div>
                )}
              </div>
              <div>
                <Label htmlFor="edit-description">Description</Label>
                <Textarea
                  id="edit-description"
                  value={description}
                  onChange={(e) => { setDescription(e.target.value); markDirty('description'); }}
                  rows={2}
                  className={dirtyFields.has('description') ? 'border-amber-400' : ''}
                  placeholder="Invoice description or notes..."
                />
              </div>
            </div>
          </section>

          <Separator />

          {/* ── Dates ── */}
          <section>
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">Dates</h3>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label htmlFor="edit-issue-date">Issue Date</Label>
                <DatePicker
                  id="edit-issue-date"
                  value={issueDate}
                  onChange={(iso) => { setIssueDate(iso); markDirty('issue_date'); }}
                  className={dirtyFields.has('issue_date') ? 'border-amber-400' : ''}
                />
              </div>
              <div>
                <Label htmlFor="edit-due-date">Due Date</Label>
                <DatePicker
                  id="edit-due-date"
                  value={dueDate}
                  onChange={(iso) => { setDueDate(iso); markDirty('due_date'); }}
                  className={dirtyFields.has('due_date') ? 'border-amber-400' : ''}
                />
              </div>
              <div>
                <Label htmlFor="edit-payment-date">Payment Date</Label>
                <DatePicker
                  id="edit-payment-date"
                  value={paymentDate}
                  onChange={(iso) => { setPaymentDate(iso); markDirty('payment_date'); }}
                  className={dirtyFields.has('payment_date') ? 'border-amber-400' : ''}
                />
                <p className="text-xs text-muted-foreground mt-1">Leave empty if not yet paid</p>
              </div>
            </div>
          </section>

          <Separator />

          {/* ── Financial ── */}
          <section>
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">Financial</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="edit-subtotal">Subtotal</Label>
                <Input
                  id="edit-subtotal"
                  type="number"
                  step="0.01"
                  value={subtotal}
                  onChange={(e) => { setSubtotal(e.target.value); markDirty('subtotal'); }}
                  className={dirtyFields.has('subtotal') ? 'border-amber-400' : ''}
                />
              </div>
              <div>
                <Label htmlFor="edit-service-charge">Service Charge</Label>
                <Input
                  id="edit-service-charge"
                  type="number"
                  step="0.01"
                  value={serviceCharge}
                  onChange={(e) => { setServiceCharge(e.target.value); markDirty('service_charge'); }}
                  className={dirtyFields.has('service_charge') ? 'border-amber-400' : ''}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 mt-4">
              <div>
                <Label htmlFor="edit-vat-type">VAT Type</Label>
                <Select
                  value={vatType}
                  onValueChange={(v) => { setVatType(v); markDirty('vat_type'); }}
                >
                  <SelectTrigger className={dirtyFields.has('vat_type') ? 'border-amber-400' : ''}>
                    <SelectValue placeholder="Select VAT type" />
                  </SelectTrigger>
                  <SelectContent>
                    {VAT_TYPES.map(vt => (
                      <SelectItem key={vt.value} value={vt.value}>{vt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="edit-vat-rate">VAT Rate (%)</Label>
                <Input
                  id="edit-vat-rate"
                  type="number"
                  step="0.01"
                  value={vatRate}
                  onChange={(e) => { setVatRate(e.target.value); markDirty('vat_rate'); }}
                  className={dirtyFields.has('vat_rate') ? 'border-amber-400' : ''}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 mt-4">
              <div>
                <Label htmlFor="edit-vat-amount">
                  VAT Amount
                  {!dirtyFields.has('vat_amount') && (
                    <span className="text-xs text-muted-foreground ml-1">(calc: {formatCurrency(calculatedVatAmount)})</span>
                  )}
                </Label>
                <Input
                  id="edit-vat-amount"
                  type="number"
                  step="0.01"
                  value={vatAmount}
                  onChange={(e) => { setVatAmount(e.target.value); markDirty('vat_amount'); }}
                  className={dirtyFields.has('vat_amount') ? 'border-amber-400' : ''}
                />
              </div>
              <div>
                <Label htmlFor="edit-total">
                  Total
                  {!dirtyFields.has('total') && (
                    <span className="text-xs text-muted-foreground ml-1">(calc: {formatCurrency(calculatedTotal)})</span>
                  )}
                </Label>
                <Input
                  id="edit-total"
                  type="number"
                  step="0.01"
                  value={total}
                  onChange={(e) => { setTotal(e.target.value); markDirty('total'); }}
                  className={dirtyFields.has('total') ? 'border-amber-400' : ''}
                />
              </div>
            </div>

            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleRecalculate}
              className="w-full mt-4"
            >
              <Calculator className="h-4 w-4 mr-2" />
              Recalculate VAT & Total from Subtotal + Rate
            </Button>

            <div className="grid grid-cols-2 gap-4 mt-4">
              <div>
                <Label htmlFor="edit-fee-split-pct">Fee Split %</Label>
                <Input
                  id="edit-fee-split-pct"
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={feeSplitPercentage}
                  onChange={(e) => { setFeeSplitPercentage(e.target.value); markDirty('fee_split_percentage'); }}
                  className={dirtyFields.has('fee_split_percentage') ? 'border-amber-400' : ''}
                />
              </div>
              <div>
                <Label htmlFor="edit-fee-split-amt">Fee Split Amount</Label>
                <Input
                  id="edit-fee-split-amt"
                  type="number"
                  step="0.01"
                  value={feeSplitAmount}
                  onChange={(e) => { setFeeSplitAmount(e.target.value); markDirty('fee_split_amount'); }}
                  className={dirtyFields.has('fee_split_amount') ? 'border-amber-400' : ''}
                />
              </div>
            </div>

            {/* Balance summary */}
            <div className="rounded-lg bg-muted/50 p-4 space-y-2 mt-4">
              <h4 className="text-sm font-semibold">Balance Summary</h4>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <span className="text-muted-foreground">Total:</span>
                <span className="text-right font-medium">{formatCurrency(total)}</span>
                <span className="text-muted-foreground">Applied Down Payments:</span>
                <span className="text-right">{formatCurrency(appliedDownPaymentsAmount)}</span>
                <span className="text-muted-foreground">Applied Credit Notes:</span>
                <span className="text-right">{formatCurrency(appliedCreditNotesAmount)}</span>
                <Separator className="col-span-2" />
                <span className="text-muted-foreground font-semibold">Balance Due:</span>
                <span className="text-right font-bold">{formatCurrency(calculatedBalanceDue)}</span>
              </div>
            </div>
          </section>

          <Separator />

          {/* ── Additional Details ── */}
          <section>
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">Additional Details</h3>

            {currentType === 'credit_note' && (
              <div className="mb-4">
                <Label htmlFor="edit-credit-reason">Credit Reason</Label>
                <Select
                  value={creditReason}
                  onValueChange={(v) => { setCreditReason(v); markDirty('credit_reason'); }}
                >
                  <SelectTrigger className={dirtyFields.has('credit_reason') ? 'border-amber-400' : ''}>
                    <SelectValue placeholder="Select reason" />
                  </SelectTrigger>
                  <SelectContent>
                    {CREDIT_REASONS.map(r => (
                      <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="flex items-center gap-3 mb-4">
              <input
                id="edit-email-notif"
                type="checkbox"
                checked={sendEmailNotification}
                onChange={(e) => { setSendEmailNotification(e.target.checked); markDirty('send_email_notification'); }}
                className="rounded"
              />
              <Label htmlFor="edit-email-notif">Send email notification on status changes</Label>
            </div>

            {/* Deduction toggle for standalone invoices */}
            {currentType === 'invoice' && !invoice.timesheets?.length && (
              <div className="rounded-lg border p-4 space-y-2 mb-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="edit-deduct">Deduct from next invoice</Label>
                    <p className="text-xs text-muted-foreground">
                      This standalone invoice amount will be deducted from the next timesheet-based invoice for the same matter/client.
                    </p>
                  </div>
                  <input
                    id="edit-deduct"
                    type="checkbox"
                    checked={deductFromNextInvoice}
                    onChange={(e) => { setDeductFromNextInvoice(e.target.checked); markDirty('deduct_from_next_invoice'); }}
                    className="rounded"
                  />
                </div>
                {invoice.deducted_amount && Number(invoice.deducted_amount) > 0 && (
                  <p className="text-sm text-green-600">
                    Already deducted: {formatCurrency(invoice.deducted_amount)}
                  </p>
                )}
              </div>
            )}

            {/* Metadata */}
            <div className="rounded-lg bg-muted/50 p-4 space-y-2">
              <h4 className="text-sm font-semibold">Metadata</h4>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <span className="text-muted-foreground">Created:</span>
                <span>{invoice.created_at ? format(parseISO(invoice.created_at), 'dd/MM/yyyy HH:mm') : 'N/A'}</span>
                <span className="text-muted-foreground">Updated:</span>
                <span>{invoice.updated_at ? format(parseISO(invoice.updated_at), 'dd/MM/yyyy HH:mm') : 'N/A'}</span>
                {invoice.email_sent_at && (
                  <>
                    <span className="text-muted-foreground">Email Sent:</span>
                    <span>{format(parseISO(invoice.email_sent_at), 'dd/MM/yyyy HH:mm')}</span>
                  </>
                )}
                {invoice.timesheets && invoice.timesheets.length > 0 && (
                  <>
                    <span className="text-muted-foreground">Timesheets:</span>
                    <span>{invoice.timesheets.length} entries</span>
                  </>
                )}
              </div>
            </div>
          </section>
        </div>

        <DialogFooter className="flex justify-between items-center gap-2">
          <div className="text-xs text-muted-foreground">
            {hasChanges
              ? `${dirtyFields.size} field${dirtyFields.size !== 1 ? 's' : ''} modified`
              : 'No changes yet'}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} disabled={isSaving}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={isSaving || !hasChanges}
            >
              {isSaving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-primary-foreground border-t-transparent mr-2" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Save Changes
                </>
              )}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
